import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderNav from "@/components/HeaderNav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";

const FREE_FEATURES = [
  "Top 5 personalized matches",
  "Full hustle directory access",
  "Community feed",
];

const PRO_FEATURES = [
  "All 22+ hustle matches unlocked",
  "AI-generated Claude blueprints",
  "Income tracker with charts",
  "Unlimited re-quizzes",
  "Priority new hustle drops",
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState("monthly"); // monthly | yearly
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) { nav("/"); return; }
    setLoading(true);
    try {
      const lookup_key = plan === "yearly" ? "pro_yearly" : "pro_monthly";
      const { data } = await api.post("/payments/checkout", {
        lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <HeaderNav/>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 lg:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="caption">Pricing</div>
          <h1 className="font-display text-4xl sm:text-6xl font-black leading-tight mt-3">
            Free to start. Pro to <span className="italic text-[var(--copper)]">earn</span>.
          </h1>
          <p className="text-lg text-[var(--stone)] mt-4">
            Cancel anytime. 7-day no-questions refunds.
          </p>
        </div>

        <div className="flex justify-center mt-8">
          <div className="inline-flex bg-white border border-[var(--border-warm)] rounded-full p-1">
            <button onClick={()=>setPlan("monthly")} data-testid="plan-monthly-btn"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${plan==="monthly"?"bg-[var(--forest)] text-[var(--alabaster)]":"text-[var(--charcoal)]"}`}>
              Monthly
            </button>
            <button onClick={()=>setPlan("yearly")} data-testid="plan-yearly-btn"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${plan==="yearly"?"bg-[var(--forest)] text-[var(--alabaster)]":"text-[var(--charcoal)]"}`}>
              Yearly <span className="text-[var(--copper)]">-30%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-10 max-w-4xl mx-auto">
          <div className="card-warm p-8">
            <div className="caption">Starter</div>
            <div className="font-display text-5xl font-black mt-2">$0</div>
            <div className="text-sm text-[var(--stone)]">forever</div>
            <ul className="mt-6 space-y-2">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex gap-2 items-center text-sm">
                  <Check size={16} className="text-[var(--forest)]"/> {f}
                </li>
              ))}
            </ul>
            <button className="btn-outline w-full mt-8 justify-center" onClick={()=>nav("/app")}>
              Keep using free
            </button>
          </div>

          <div className="card-warm p-8 relative" style={{background:"var(--forest)", color:"var(--alabaster)", borderColor:"var(--forest)"}}>
            <span className="absolute top-4 right-4 tag-pill" style={{background:"var(--copper)", color:"#fff", border:"none"}}>
              <Sparkles size={12}/> Most popular
            </span>
            <div className="caption" style={{color:"#D96B27"}}>Pro</div>
            <div className="font-display text-5xl font-black mt-2 font-mono">
              ${plan === "yearly" ? "13.25" : "19"}
              <span className="text-lg font-normal opacity-70">/mo</span>
            </div>
            <div className="text-sm opacity-80">
              {plan === "yearly" ? "billed $159/year" : "billed monthly"}
            </div>
            <ul className="mt-6 space-y-2">
              {PRO_FEATURES.map(f => (
                <li key={f} className="flex gap-2 items-center text-sm">
                  <Check size={16} className="text-[var(--copper)]"/> {f}
                </li>
              ))}
            </ul>
            <button onClick={checkout} disabled={loading || user?.pro} className="btn-copper w-full mt-8 justify-center" data-testid="checkout-btn">
              {user?.pro ? "You're already Pro ✓" : (loading ? "Loading…" : "Get Pro")}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-[var(--stone)] mt-10">
          Payments processed via Stripe. Test with card <span className="font-mono">4242 4242 4242 4242</span>, any future date, any CVC.
        </p>
      </div>
    </div>
  );
}
